/**
 * Local Character Service
 * Manages local character storage and retrieval from JSON files and localStorage
 *
 * Validates character data on load using Zod schemas.
 * Checks for missing purchaseHistory, incomplete data, and invalid IDs.
 * Logs warnings for invalid data but allows load to proceed with defaults.
 *
 * Requirements: 2.4, 8.5
 */

import type { Character } from '../types/character';
import { characterValidationService } from './CharacterValidationService';
import captainMordianVexData from '../data/characters/captain-mordian-vex.json';
import severinaKaelData from '../data/characters/severina-kael.json';

export interface LocalCharacterInfo {
  id: string;
  name: string;
  career: string;
  lastModified: string;
  source: 'bundled' | 'localStorage';
}

class LocalCharacterService {
  private static instance: LocalCharacterService;

  public static getInstance(): LocalCharacterService {
    if (!LocalCharacterService.instance) {
      LocalCharacterService.instance = new LocalCharacterService();
    }
    return LocalCharacterService.instance;
  }

  /**
   * Get list of all available local characters
   */
  public getAvailableCharacters(): LocalCharacterInfo[] {
    const characters: LocalCharacterInfo[] = [];

    // Add bundled characters
    characters.push({
      id: captainMordianVexData.id,
      name: captainMordianVexData.name,
      career: captainMordianVexData.career.name,
      lastModified: captainMordianVexData.lastModified,
      source: 'bundled'
    });

    characters.push({
      id: severinaKaelData.id,
      name: severinaKaelData.name,
      career: severinaKaelData.career.name,
      lastModified: severinaKaelData.lastModified,
      source: 'bundled'
    });

    // Add localStorage characters
    const localStorageCharacters = this.getLocalStorageCharacters();
    characters.push(...localStorageCharacters);

    return characters.sort(
      (a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
    );
  }

  /**
   * Load a character by ID
   */
  public async loadCharacter(id: string): Promise<Character | null> {
    // Check bundled characters first
    if (id === captainMordianVexData.id) {
      return this.convertJsonToCharacter(captainMordianVexData);
    }
    if (id === severinaKaelData.id) {
      return this.convertJsonToCharacter(severinaKaelData);
    }

    // Check localStorage
    try {
      const stored = localStorage.getItem(`rogue-trader-character-${id}`);
      if (stored) {
        const characterData = JSON.parse(stored);
        return this.convertJsonToCharacter(characterData);
      }
    } catch (error) {
      console.error('Failed to load character from localStorage:', error);
    }

    return null;
  }

  /**
   * Save a character to localStorage
   */
  public saveCharacter(character: Character): void {
    try {
      const characterData = {
        ...character,
        lastModified: new Date().toISOString()
      };

      localStorage.setItem(`rogue-trader-character-${character.id}`, JSON.stringify(characterData));

      // Also update the current character
      localStorage.setItem('rogue-trader-current-character', JSON.stringify(characterData));
    } catch (error) {
      console.error('Failed to save character to localStorage:', error);
      throw new Error('Failed to save character locally');
    }
  }

  /**
   * Delete a character from localStorage
   */
  public deleteCharacter(id: string): boolean {
    try {
      // Don't allow deletion of bundled characters
      if (id === captainMordianVexData.id || id === severinaKaelData.id) {
        return false;
      }

      localStorage.removeItem(`rogue-trader-character-${id}`);
      return true;
    } catch (error) {
      console.error('Failed to delete character:', error);
      return false;
    }
  }

  /**
   * Create a new character with a unique ID
   */
  public createNewCharacter(name: string): Character {
    const now = new Date();
    const id = `character-${Date.now()}`;

    return {
      id,
      name,
      career: {
        id: '',
        name: '',
        description: '',
        startingSkills: [],
        startingTalents: [],
        startingEquipment: [],
        aptitudes: [],
        rulebook: ''
      },
      homeworld: {
        id: '',
        name: '',
        description: '',
        characteristicModifiers: {},
        startingSkills: [],
        startingTalents: [],
        startingTraits: [],
        startingEquipment: [],
        rulebook: ''
      },
      birthright: {
        id: '',
        name: '',
        description: '',
        benefit: '',
        rulebook: ''
      },
      lure: {
        id: '',
        name: '',
        description: '',
        benefit: '',
        rulebook: ''
      },
      trials: {
        id: '',
        name: '',
        description: '',
        benefit: '',
        rulebook: ''
      },
      motivation: {
        id: '',
        name: '',
        description: '',
        benefit: '',
        rulebook: ''
      },
      characteristics: {
        weaponSkill: 30,
        ballisticSkill: 30,
        strength: 30,
        toughness: 30,
        agility: 30,
        intelligence: 30,
        perception: 30,
        willpower: 30,
        fellowship: 30,
        influence: 30
      },
      derivedStats: {
        wounds: 0,
        fatigue: 0,
        corruption: 0,
        insanity: 0,
        profitFactor: 0,
        initiative: 0,
        movementHalf: 0,
        movementFull: 0,
        movementCharge: 0,
        movementRun: 0
      },
      experience: {
        total: 0,
        spent: 0,
        available: 0
      },
      skills: [],
      talents: [],
      traits: [],
      equipment: [],
      weapons: [],
      armor: [],
      enabledRulebooks: ['core'],
      manualAdjustments: [],
      conditionalModifiers: [],
      schemaVersion: 1,
      createdAt: now,
      lastModified: now
    };
  }

  /**
   * Get characters stored in localStorage
   */
  private getLocalStorageCharacters(): LocalCharacterInfo[] {
    const characters: LocalCharacterInfo[] = [];

    try {
      // Scan localStorage for character keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          key.startsWith('rogue-trader-character-') &&
          key !== 'rogue-trader-character-captain-mordian-vex' &&
          key !== 'rogue-trader-character-severina-kael'
        ) {
          const stored = localStorage.getItem(key);
          if (stored) {
            const characterData = JSON.parse(stored);
            characters.push({
              id: characterData.id,
              name: characterData.name,
              career: characterData.career?.name || 'Unknown',
              lastModified:
                characterData.lastModified || characterData.createdAt || new Date().toISOString(),
              source: 'localStorage'
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to scan localStorage for characters:', error);
    }

    return characters;
  }

  /**
   * Convert JSON data to Character object with proper date conversion.
   * Validates character data using Zod schemas and logs warnings.
   * Ensures character data is self-contained with all required fields.
   *
   * Requirements: 2.4, 2.5, 8.5
   */
  private convertJsonToCharacter(data: any): Character {
    // Validate character data and get warnings/fixes
    const validationResult = characterValidationService.validateCharacterOnImport(data);

    // Log validation results for debugging
    characterValidationService.logValidationResult(validationResult);

    // Use validated/fixed data if available
    const validatedData = validationResult.character || data;

    // Migrate legacy homeworldTalentChoice (singular) to homeworldTalentChoices (array)
    if (validatedData.homeworldTalentChoice && !validatedData.homeworldTalentChoices) {
      validatedData.homeworldTalentChoices = [validatedData.homeworldTalentChoice];
      delete validatedData.homeworldTalentChoice;
    }

    return {
      ...validatedData,
      createdAt: new Date(data.createdAt),
      lastModified: new Date(data.lastModified),
      // Ensure skills have purchaseHistory initialized with proper dates
      skills: (validatedData.skills || []).map((skill: any) => ({
        ...skill,
        purchaseHistory: (skill.purchaseHistory || []).map((purchase: any) => ({
          ...purchase,
          purchasedAt:
            purchase.purchasedAt instanceof Date
              ? purchase.purchasedAt
              : new Date(purchase.purchasedAt)
        }))
      })),
      // Ensure talents have purchaseHistory and purchaseCount initialized with proper dates
      talents: (validatedData.talents || []).map((talent: any) => {
        const purchaseHistory = (talent.purchaseHistory || []).map((purchase: any) => ({
          ...purchase,
          purchasedAt:
            purchase.purchasedAt instanceof Date
              ? purchase.purchasedAt
              : new Date(purchase.purchasedAt)
        }));
        return {
          ...talent,
          purchaseHistory,
          purchaseCount: talent.purchaseCount ?? purchaseHistory.length
        };
      }),
      // Ensure manual adjustments have proper date conversion
      manualAdjustments: (data.manualAdjustments || []).map((adj: any) => ({
        ...adj,
        timestamp: new Date(adj.timestamp)
      }))
    };
  }
}

export const localCharacterService = LocalCharacterService.getInstance();
